import { useState, useCallback, useRef } from "react";
import { useStripe, useElements } from "@stripe/react-stripe-js";
import { StripeError } from "@stripe/stripe-js";
import { enqueueSnackbar } from "@powerdiary/components-library/lib/components/Snackbar";
import { useCreateOrUpdateCustomFormCustomerCreditCard } from "./useCreateOrUpdateCustomFormCustomerCreditCard";
import { useSetupSCAIntent } from "../../Shared/StripePayment/useSetupSCAIntent";

// Confirmation failures that leave the SetupIntent re-confirmable, so a retry can replay
// it by reusing the same idempotency key.
const RECONFIRMABLE_CONFIRM_ERROR_TYPES: StripeError["type"][] = ["card_error", "validation_error"];

interface UseSaveCreditCardParams {
    publicId: string;
    practitionerId?: number;
    onCardSaved: (value: string) => void;
}

export function useSaveCreditCard({ publicId, practitionerId, onCardSaved }: UseSaveCreditCardParams) {
    const [isSaving, setIsSaving] = useState(false);
    const { setupSCAIntent } = useSetupSCAIntent(publicId);
    const { createOrUpdateCustomFormCustomerCreditCard } = useCreateOrUpdateCustomFormCustomerCreditCard(publicId);
    const idempotencyKeyRef = useRef<string>(crypto.randomUUID());
    const stripe = useStripe();
    const elements = useElements();

    const handleAddCard = useCallback(async () => {
        setIsSaving(true);
        try {
            const cardElement = elements.getElement("cardNumber");

            // Reject an invalid card client-side before the backend round-trip.
            const tokenResult = await stripe.createToken(cardElement);
            if (tokenResult?.error) {
                enqueueSnackbar(tokenResult.error.message ?? "Please check your card details.", {
                    variant: "error",
                });
                return;
            }

            const setupResult = await setupSCAIntent({
                idempotencyKey: idempotencyKeyRef.current,
                integrationId: 0,
                stripeConnectUserId: null,
            });

            const confirmResult = await stripe.confirmCardSetup(setupResult.clientSecret, {
                payment_method: {
                    card: cardElement,
                },
            });
            if (confirmResult?.error) {
                // No cleanup on failure: SetupSCAIntent does not detach the client's saved cards
                // (PD-17945), so the entered details and any saved card stay valid for a retry.
                if (!RECONFIRMABLE_CONFIRM_ERROR_TYPES.includes(confirmResult.error.type)) {
                    // Any other failure (e.g. the connection dropped after Stripe processed the
                    // confirmation) may have consumed the intent, so retries need a fresh key.
                    idempotencyKeyRef.current = crypto.randomUUID();
                }
                enqueueSnackbar(confirmResult.error.message ?? "Failed to confirm card details.", {
                    variant: "error",
                });
                return;
            }

            // A confirmed SetupIntent cannot be confirmed again; mint a fresh key so a retry after
            // a failed persist below creates a new SetupIntent instead of replaying this one.
            idempotencyKeyRef.current = crypto.randomUUID();

            await createOrUpdateCustomFormCustomerCreditCard({
                paymentMethodId: confirmResult.setupIntent.payment_method as string,
                practitionerId: practitionerId,
            });

            onCardSaved("Card Saved");
        } catch {
            // A throw here can also hide a confirmation Stripe processed (consuming the
            // intent), so the next attempt must not replay this key either.
            idempotencyKeyRef.current = crypto.randomUUID();
            enqueueSnackbar("Failed to save card details. Please try again.", {
                variant: "error",
            });
        } finally {
            setIsSaving(false);
        }
    }, [stripe, elements, practitionerId, setupSCAIntent, createOrUpdateCustomFormCustomerCreditCard, onCardSaved]);

    return {
        handleAddCard,
        isSaving,
    };
}
